/**
 * Shared form fields for staff create/edit.
 * Staff member limits come from the owning plan (staff_member_limit);
 * the API rejects creation beyond the limit with a localized message.
 */

import {
  TextInput,
  SelectInput,
  email,
  required,
  useGetList,
  useTranslate,
} from 'react-admin';
import { Alert } from '@mui/material';
import { useWatch } from 'react-hook-form';
import { AUTH_PROVISIONING_ENABLED } from '@/config/constants';

const DepartmentSelect = () => {
  const organizationId = useWatch({ name: 'organization_id' });
  const { data: departments } = useGetList('departments', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'name', order: 'ASC' },
  });
  const choices = (departments ?? [])
    .filter((dept: any) => !organizationId || dept.organization_id === organizationId)
    .map((dept: any) => ({ id: dept.department_id, name: dept.name }));
  return <SelectInput source="department_id" choices={choices} />;
};

export const StaffFormFields = ({ isCreate }: { isCreate: boolean }) => {
  const translate = useTranslate();
  const { data: organizations } = useGetList('organizations', {
    pagination: { page: 1, perPage: 500 },
    sort: { field: 'full_name', order: 'ASC' },
  });

  return (
    <>
      {isCreate && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {translate('resources.staff.plan_limit_note')}
        </Alert>
      )}
      <TextInput source="full_name" validate={required()} />
      <TextInput source="email" type="email" validate={[required(), email()]} />
      {isCreate && AUTH_PROVISIONING_ENABLED && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {translate('resources.staff.activation_note')}
        </Alert>
      )}
      {isCreate && !AUTH_PROVISIONING_ENABLED && (
        <TextInput
          source="password"
          type="password"
          validate={required()}
          autoComplete="new-password"
        />
      )}
      <TextInput source="contact_no" />
      <SelectInput
        source="organization_id"
        validate={isCreate ? required() : undefined}
        choices={(organizations ?? []).map((org: any) => ({
          id: org.user_id,
          name: org.company_name || org.full_name,
        }))}
      />
      <DepartmentSelect />
      {!isCreate && (
        <SelectInput
          source="status"
          choices={[
            { id: 1, name: translate('common.status.active', { _: 'Activo' }) },
            { id: 0, name: translate('common.status.inactive', { _: 'Inactivo' }) },
          ]}
        />
      )}
    </>
  );
};

export default StaffFormFields;
